import { GameState, StatusEffectSnapshot } from '../model/gamestate/GameState';
import { t } from '../i18n';
import { localizeSkillName, localizeSkillDescription } from '../i18n/content';
import { ActionTooltip } from './ActionTooltip';
import { createSkillEmojiBox, isGreaterSkill } from './skillVariants';

export interface StatusBarSnapshot {
  hp?: number;
  maxHp?: number;
  mana?: number;
  maxMana?: number;
  stamina?: number;
  maxStamina?: number;
  statusEffects?: StatusEffectSnapshot[];
}

export interface StatusBarHandle {
  update(snapshot: StatusBarSnapshot): void;
  clear(): void;
  destroy(): void;
}

const DEFAULT_RESOURCE_VALUE = 10_000;

function clampRatio(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(0, Math.min(1, current / max));
}

function formatValue(value: number): string {
  return Number.isFinite(value) ? Math.round(value).toLocaleString() : '0';
}

export function createStatusBar(
  container: HTMLElement,
  gameState: GameState,
  effectsContainer?: HTMLElement,
  onCancelStatusEffect?: (effectId: string) => void
): StatusBarHandle {
  const root = document.createElement('div');
  root.className = 'status-bar-panel';
  const tooltip = new ActionTooltip();

  const hpRow = document.createElement('div');
  hpRow.className = 'status-row';
  const hpLabel = document.createElement('span');
  hpLabel.className = 'status-label';
  hpLabel.textContent = 'HP';
  const hpValues = document.createElement('span');
  hpValues.className = 'status-values';
  const hpTrack = document.createElement('div');
  hpTrack.className = 'status-track';
  const hpFill = document.createElement('div');
  hpFill.className = 'status-fill hp-fill';
  hpTrack.appendChild(hpFill);
  hpRow.append(hpLabel, hpValues, hpTrack);

  const manaRow = document.createElement('div');
  manaRow.className = 'status-row';
  const manaLabel = document.createElement('span');
  manaLabel.className = 'status-label';
  manaLabel.textContent = 'Mana';
  const manaValues = document.createElement('span');
  manaValues.className = 'status-values';
  const manaTrack = document.createElement('div');
  manaTrack.className = 'status-track';
  const manaFill = document.createElement('div');
  manaFill.className = 'status-fill mana-fill';
  manaTrack.appendChild(manaFill);
  manaRow.append(manaLabel, manaValues, manaTrack);

  const staminaRow = document.createElement('div');
  staminaRow.className = 'status-row';
  const staminaLabel = document.createElement('span');
  staminaLabel.className = 'status-label';
  staminaLabel.textContent = 'Stamina';
  const staminaValues = document.createElement('span');
  staminaValues.className = 'status-values';
  const staminaTrack = document.createElement('div');
  staminaTrack.className = 'status-track';
  const staminaFill = document.createElement('div');
  staminaFill.className = 'status-fill';
  staminaFill.style.background = 'linear-gradient(90deg, #22c55e, #86efac)';
  staminaTrack.appendChild(staminaFill);
  staminaRow.append(staminaLabel, staminaValues, staminaTrack);

  root.append(hpRow, manaRow, staminaRow);
  container.appendChild(root);

  const fallbackEmoji: Record<string, string> = {
    'dot': '🤢',
    'taunt': '😡',
    'absorb_poison': '🛡️',
    'coup_de_grace_mark': '🎯',
    'voodoo_curse_link': '💀',
  };
  let isHoveringEffects = false;

  if (effectsContainer) {
    effectsContainer.addEventListener('mouseenter', () => {
      isHoveringEffects = true;
    });
    effectsContainer.addEventListener('mouseleave', () => {
      isHoveringEffects = false;
    });
  }

  const renderStatusEffects = (effects: StatusEffectSnapshot[]): void => {
    if (!effectsContainer) return;
    if (isHoveringEffects) return;
    effectsContainer.innerHTML = '';
    const currentTick = gameState.getTick();

    for (const effect of effects) {
      const hasExpiry = effect.expiresAtTick != null;
      const remainingTicks = hasExpiry ? Math.max(0, (effect.expiresAtTick as number) - currentTick) : null;
      if (hasExpiry && (remainingTicks as number) <= 0) continue;
      const skill = gameState.getKnownSkills().find(s => s.skillId === effect.skillId);
      const emoji = skill?.emoji || fallbackEmoji[effect.effectType] || '✨';

      const iconEl = document.createElement('div');
      iconEl.className = 'status-effect-icon';
      iconEl.appendChild(createSkillEmojiBox(emoji, skill ? isGreaterSkill(skill.skillId) : false));
      const canCancel = Boolean(skill?.tags.includes('Support'));

      if (remainingTicks != null) {
        const badge = document.createElement('span');
        badge.className = 'duration-badge';
        badge.textContent = String(remainingTicks);
        iconEl.appendChild(badge);
      }

      iconEl.addEventListener('mouseenter', (e) => {
        const title = skill ? localizeSkillName(skill.skillId, skill.displayName) : effect.effectType;
        const desc = skill ? localizeSkillDescription(skill.skillId, skill.description) : t(`content.status_effects.descriptions.${effect.effectType}`);
        const selfEntityId = gameState.getSelfEntityId();
        const sourceLabel = effect.sourceId === selfEntityId ? 'You' : effect.sourceId;
        const statLines: string[] = [];
        statLines.push(`Source: ${sourceLabel}`);
        if (remainingTicks != null) {
          statLines.push(t('character.statCooldown', { value: remainingTicks }));
        }

        tooltip.show({
          title,
          description: desc,
          statLines
        }, e.clientX, e.clientY);
      });
      iconEl.addEventListener('mousemove', (e) => tooltip.move(e.clientX, e.clientY));
      iconEl.addEventListener('mouseleave', () => tooltip.hide());
      iconEl.addEventListener('mousedown', () => tooltip.hide());
      if (canCancel && onCancelStatusEffect) {
        iconEl.addEventListener('contextmenu', (event) => {
          event.preventDefault();
          onCancelStatusEffect(effect.effectId);
        });
      }

      effectsContainer.appendChild(iconEl);
    }
  };

  const update = (snapshot: StatusBarSnapshot): void => {
    if (typeof snapshot.hp === 'number' && typeof snapshot.maxHp === 'number') {
      hpValues.textContent = `${formatValue(snapshot.hp)} / ${formatValue(snapshot.maxHp)}`;
      hpFill.style.width = `${clampRatio(snapshot.hp, snapshot.maxHp) * 100}%`;
    }

    if (typeof snapshot.mana === 'number' && typeof snapshot.maxMana === 'number') {
      manaValues.textContent = `${formatValue(snapshot.mana)} / ${formatValue(snapshot.maxMana)}`;
      manaFill.style.width = `${clampRatio(snapshot.mana, snapshot.maxMana) * 100}%`;
    }

    if (typeof snapshot.stamina === 'number' && typeof snapshot.maxStamina === 'number') {
      staminaValues.textContent = `${formatValue(snapshot.stamina)} / ${formatValue(snapshot.maxStamina)}`;
      staminaFill.style.width = `${clampRatio(snapshot.stamina, snapshot.maxStamina) * 100}%`;
    }

    if (snapshot.statusEffects) {
      renderStatusEffects(snapshot.statusEffects);
    }
  };

  const clear = (): void => {
    hpValues.textContent = '';
    manaValues.textContent = '';
    staminaValues.textContent = '';
    hpFill.style.width = '0%';
    manaFill.style.width = '0%';
    staminaFill.style.width = '0%';
    if (effectsContainer) {
      effectsContainer.innerHTML = '';
    }
  };

  return {
    update,
    clear,
    destroy: () => {
      root.remove();
    }
  };
}
